import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Bookmark, Users, X, Trash2, ChevronRight, Loader2 } from 'lucide-react';

/**
 * Panel lateral con la lista de marcadores del documento.
 * - Click en un item → salta a esa página (vía onJumpToPage).
 * - Solo el autor puede borrar (es_mio=true).
 * - Compartidos del taller se diferencian con icono Users + nombre del autor.
 */
const PDFBookmarksPanel = ({
  show, onClose,
  marcadores, loading,
  onJumpToPage, onDelete,
  toolbarHeight = 56,
}) => {
  if (!show) return null;

  return (
    <div className="fixed right-0 bottom-0 w-80 max-w-[90vw] bg-slate-900/95 border-l border-slate-700 backdrop-blur-sm z-50 flex flex-col shadow-2xl"
      style={{ top: `${toolbarHeight}px` }}
      data-testid="bookmarks-panel">
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Bookmark className="h-5 w-5 text-amber-400" />
          <h3 className="text-white font-semibold">Marcadores</h3>
          {marcadores.length > 0 && (
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
              {marcadores.length}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mb-2" />
            <p className="text-sm">Cargando marcadores...</p>
          </div>
        ) : marcadores.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <Bookmark className="h-10 w-10 text-slate-600 mb-3" />
            <p className="text-slate-300 text-sm font-medium">
              Aún no hay marcadores en este documento.
            </p>
            <p className="text-slate-500 text-xs mt-2">
              Usá el botón <Bookmark className="inline h-3 w-3 mx-1" /> de la barra para agregar el primero.
            </p>
          </div>
        ) : (
          <ul className="space-y-1">
            {marcadores.map((m) => (
              <li key={m.id}>
                <div
                  className={`group flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                    m.es_mio
                      ? 'hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20'
                      : 'hover:bg-cyan-500/10 border border-transparent hover:border-cyan-500/20'
                  }`}
                  onClick={() => onJumpToPage(m.pagina)}
                  data-testid={`bookmark-item-${m.id}`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    {m.es_mio ? (
                      <Bookmark className="h-4 w-4 text-amber-400 fill-amber-400/40" />
                    ) : (
                      <Users className="h-4 w-4 text-cyan-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium">Pág. {m.pagina}</span>
                      {!m.es_mio && (
                        <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 text-[10px] py-0 px-1.5">
                          Compartido
                        </Badge>
                      )}
                      {m.es_mio && m.compartido && (
                        <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] py-0 px-1.5">
                          Público
                        </Badge>
                      )}
                    </div>
                    {m.nota && (
                      <p className="text-slate-300 text-xs mt-1 line-clamp-2">{m.nota}</p>
                    )}
                    {!m.es_mio && (
                      <p className="text-slate-500 text-[11px] mt-1 italic">
                        Por: {m.user_nombre}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {m.es_mio && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(m.id); }}
                        className="p-1 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        title="Eliminar marcador"
                        data-testid={`bookmark-delete-${m.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PDFBookmarksPanel;
